import React, { useState, useEffect } from 'react';
import { API_BASE } from '../../config';
import ProfileTab from '../ProfileTab';
import HealistScoreDisplay from '../HealistScoreDisplay';
import MedicalHistorySection from '../MedicalHistorySection';
import ObjectiveForm from './ObjectiveForm';
import HealthProviderForm from './HealthProviderForm';
import SendReportModal from './SendReportModal';
import ReportProgressModal from './ReportProgressModal';
import ViewReportResponsesModal from './ViewReportResponsesModal';
import ManageReportsModal from './ManageReportsModal';
import { useConfirm } from '../ConfirmProvider';

function HealthTab({ fetchWithAuth, token, setActiveTab }) {
  const confirm = useConfirm();
  const [healthSubTab, setHealthSubTab] = useState('objectives');
  const [objectives, setObjectives] = useState([]);
  const [showManageReportsModal, setShowManageReportsModal] = useState(false);
  const [showObjectiveForm, setShowObjectiveForm] = useState(false);
  const [providers, setProviders] = useState([]);
  const [showProviderForm, setShowProviderForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [showSendReportModal, setShowSendReportModal] = useState(false);
  const [showReportResponsesModal, setShowReportResponsesModal] = useState(false);
  const [showReportProgressModal, setShowReportProgressModal] = useState(false);
  const [reportProgress, setReportProgress] = useState(0);
  const [reportComplete, setReportComplete] = useState(false);
  const [currentReportId, setCurrentReportId] = useState(null);
  const [editingObjective, setEditingObjective] = useState(null);
  // #89 Phase 4 — discovered proposals awaiting the user's decision.
  const [proposals, setProposals] = useState([]);
  const [proposalBusy, setProposalBusy] = useState(null);
  const [proposalError, setProposalError] = useState(null);

  useEffect(() => {
    loadHealthData();
  }, []);

  const loadHealthData = async () => {
    try {
      const [objectivesRes, providersRes, proposalsRes] = await Promise.all([
        fetchWithAuth(`${API_BASE}/api/health/objectives`),
        fetchWithAuth(`${API_BASE}/api/providers`),
        // #89 Phase 4 — discovered proposals. Fetched alongside rather than in a second
        // round-trip so the inbox and the objective list can never render out of step.
        fetchWithAuth(`${API_BASE}/api/health/objectives/proposals`)
      ]);

      const objectives = await objectivesRes.json();
      const providersData = providersRes.ok ? await providersRes.json() : null;
      const proposalsData = proposalsRes.ok ? await proposalsRes.json() : null;

      setObjectives(objectives);
      setProviders(providersData || []);
      setProposals((proposalsData && proposalsData.proposals) || []);
    } catch (error) {
      console.error('Failed to load health data:', error);
    }
  };

  // #89 Phase 4 — accept or decline a discovered proposal.
  // Accepting creates the objective AND its binding server-side, so it is measurable from the
  // next evaluation; an accepted-but-unmeasured goal would be worse than never proposing it.
  const respondToProposal = async (proposalId, action) => {
    setProposalBusy(proposalId);
    setProposalError(null);
    try {
      const res = await fetchWithAuth(
        `${API_BASE}/api/health/objectives/proposals/${proposalId}/${action}`, { method: 'POST' });
      if (!res.ok) {
        let detail = '';
        try { detail = (await res.json()).detail || ''; } catch (_) {}
        throw new Error(detail || `Request failed (${res.status})`);
      }
      await loadHealthData();
    } catch (err) {
      console.error('Proposal response failed:', err);
      setProposalError(err.message || 'That didn’t save. Please try again.');
    } finally {
      setProposalBusy(null);
    }
  };

  // Health Objectives handlers
  const handleEditObjective = (objective) => {
    setEditingObjective(objective);
    setShowObjectiveForm(true);
  };

  const handleDeleteObjective = async (objectiveId) => {
    if (await confirm({
      title: 'Delete health objective?',
      message: 'This health objective will be removed from your profile. This cannot be undone.',
      confirmLabel: 'Delete Objective',
      destructive: true,
    })) {
      try {
        const response = await fetchWithAuth(`${API_BASE}/api/health/objectives/${objectiveId}`, {
          method: 'DELETE'
        });
        if (response.ok) {
          loadHealthData();
        }
      } catch (error) {
        console.error('Failed to delete objective:', error);
      }
    }
  };

  const closeObjectiveForm = () => {
    setShowObjectiveForm(false);
    setEditingObjective(null);
  };

  // Prescription and Medical File handlers removed — now in standalone MedicationsPage and RecordsPage

  // Provider handlers
  const handleEditProvider = (provider) => {
    setEditingProvider(provider);
    setShowProviderForm(true);
  };

  const handleDeleteProvider = async (providerId) => {
    if (!confirm('Are you sure you want to delete this provider?')) {
      return;
    }

    try {
      const response = await fetchWithAuth(`${API_BASE}/api/providers/${providerId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadHealthData();
      }
    } catch (error) {
      console.error('Failed to delete provider:', error);
    }
  };

  const handleSendReport = async (selectedData, selectedProviders) => {
    try {
      // Show progress modal
      setShowReportProgressModal(true);
      setReportProgress(0);
      setReportComplete(false);

      // Simulate initial progress
      setReportProgress(10);

      // Call backend API
      const response = await fetchWithAuth(`${API_BASE}/api/reports/generate`, {
        method: 'POST',
        body: JSON.stringify({
          data_elements: selectedData,
          provider_ids: selectedProviders
        })
      });

      if (response.ok) {
        const result = await response.json();
        setCurrentReportId(result.report_id);

        // Start polling for progress updates
        pollReportProgress(result.report_id);
      } else {
        const error = await response.json();
        alert(`Failed to generate report: ${error.detail || 'Unknown error'}`);
        setShowReportProgressModal(false);
      }
    } catch (error) {
      console.error('Error sending report:', error);
      alert('Failed to send report. Please try again.');
      setShowReportProgressModal(false);
    }
  };

const pollReportProgress = async (reportId) => {
  const pollInterval = setInterval(async () => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/reports/${reportId}/status`);

      if (response.ok) {
        const status = await response.json();
        setReportProgress(status.progress_percentage);

        if (status.generation_status === 'completed' || status.generation_status === 'sent') {
          setReportComplete(true);
          clearInterval(pollInterval);
        } else if (status.generation_status === 'failed') {
          alert('Report generation failed. Please try again.');
          setShowReportProgressModal(false);
          clearInterval(pollInterval);
        }
      }
    } catch (error) {
      console.error('Error polling report status:', error);
      clearInterval(pollInterval);
    }
  }, 2000); // Poll every 2 seconds
};

  const closeProviderForm = () => {
    setShowProviderForm(false);
    setEditingProvider(null);
  };

  return (
    <div className="page-layout">
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Health Management</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>← Dashboard</button>
        </div>

        <div className="page-nav-bar">
          {/* 5-Tab Navigation */}
          <div className="tab-bar">
            <button
              className={`tab-item${healthSubTab === 'objectives' ? ' tab-item--active' : ''}`}
              onClick={() => setHealthSubTab('objectives')}
            >
              Health Objectives
            </button>
            <button
              className={`tab-item${healthSubTab === 'body' ? ' tab-item--active' : ''}`}
              onClick={() => setHealthSubTab('body')}
            >
              Body Characteristics
            </button>
            <button
              className={`tab-item${healthSubTab === 'score' ? ' tab-item--active' : ''}`}
              onClick={() => setHealthSubTab('score')}
            >
              Healist Score
            </button>
            <button
              className={`tab-item${healthSubTab === 'history' ? ' tab-item--active' : ''}`}
              onClick={() => setHealthSubTab('history')}
            >
              Medical History
            </button>
            <button
              className={`tab-item${healthSubTab === 'providers' ? ' tab-item--active' : ''}`}
              onClick={() => setHealthSubTab('providers')}
            >
              Physicians / Providers
            </button>
          </div>
        </div>
      </div>

      <div className="page-scroll-content">
      {/* ===== TAB 1: Health Objectives ===== */}
      {/* ===== #89 PHASE 4 — DISCOVERED PROPOSALS INBOX =====
           Sits ABOVE the objective list because it is a decision awaiting the user, not a
           record. Each card SHOWS ITS WORK — the numbers, the window, the sample size — so
           the user can evaluate the suggestion rather than take it on faith. Declining costs
           nothing and is stated plainly, because an inbox that feels like pressure gets
           ignored, and an ignored inbox is worse than none. */}
      {healthSubTab === 'objectives' && proposals.length > 0 && (
        <div className="card" data-testid="proposal-inbox">
          <div className="card-header">
            <h3 className="card-title">
              Healist noticed {proposals.length === 1 ? 'something' : `${proposals.length} things`}
            </h3>
          </div>
          <div className="card-body">
            {proposalError && (
              <div className="obj-actionError" data-testid="proposal-error">{proposalError}</div>
            )}
            {proposals.map(p => (
              <div className="prop-card" key={p.id} data-testid="proposal-card">
                <div className="prop-title">{p.title}</div>
                <p className="prop-rationale">{p.rationale}</p>
                {p.evidence && Object.keys(p.evidence).length > 0 && (
                  <div className="prop-evidence" data-testid="proposal-evidence">
                    {p.evidence.n_short != null && p.evidence.short_window_days != null && (
                      <span>{p.evidence.n_short} readings over the last {p.evidence.short_window_days} days</span>
                    )}
                    {p.evidence.anomaly_count != null && (
                      <span>{p.evidence.anomaly_count} unusual readings in {p.evidence.window_days} days</span>
                    )}
                    {p.confidence != null && <span>· confidence {Number(p.confidence).toFixed(2)}</span>}
                  </div>
                )}
                <div className="prop-actions">
                  <button className="btn btn-primary btn-sm"
                          data-testid="proposal-accept"
                          disabled={proposalBusy === p.id}
                          onClick={() => respondToProposal(p.id, 'accept')}>
                    Add this goal
                  </button>
                  <button className="btn btn-secondary btn-sm"
                          data-testid="proposal-dismiss"
                          disabled={proposalBusy === p.id}
                          onClick={() => respondToProposal(p.id, 'dismiss')}>
                    Not now
                  </button>
                  <span className="prop-note">Declining costs nothing and won’t be asked again soon.</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {healthSubTab === 'objectives' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Health Objectives</h3>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => setShowObjectiveForm(true)}
            >
              Add Objective
            </button>
          </div>

          {objectives.length === 0 ? (
            <p>No health objectives set. Add your first goal!</p>
          ) : (
            <div>
              {objectives.map(obj => (
                <div key={obj.id} className="objective-item">
                  <div className="objective-content">
                    <h4>{obj.title}</h4>
                    <p>{obj.description}</p>
                  </div>
                  <div className="objective-actions">
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleEditObjective(obj)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteObjective(obj.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== TAB 2: Body Characteristics (from Profile) ===== */}
      {healthSubTab === 'body' && (
        <ProfileTab token={token} fetchWithAuth={fetchWithAuth} sectionOnly="body" />
      )}

      {/* ===== TAB 3: Healist Score (from Profile) ===== */}
      {healthSubTab === 'score' && (
        <div className="pt-healistScoreWrapper">
          <HealistScoreDisplay
            token={token}
            fetchWithAuth={fetchWithAuth}
            showRecommendations={true}
          />
        </div>
      )}

      {/* ===== TAB 4: Medical History ===== */}
      {healthSubTab === 'history' && (
        <MedicalHistorySection fetchWithAuth={fetchWithAuth} />
      )}

      {/* ===== TAB 5: Physicians / Providers ===== */}
      {healthSubTab === 'providers' && (
        <>
          {/* Physicians / Providers Section */}
      <div className="card">
        <h3 className="card-title">Physicians / Providers</h3>

        {/* Button Row */}
        <div className="btn-row">
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setShowProviderForm(true)}
          >
            Add Provider
          </button>

        </div>

        <hr className="divider" />

        {providers.length === 0 ? (
          <p>No healthcare providers recorded. Add your first provider!</p>
        ) : (
          <div>
            {providers.map(provider => (
              <div key={provider.id} className="objective-item">
                <div className="objective-content">
                  <h4>{provider.provider_name}</h4>
                  <p>{provider.provider_type}</p>
                  {provider.provider_specialty && (
                    <p>Specialty: {provider.provider_specialty}</p>
                  )}
                </div>
                <div className="objective-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleEditProvider(provider)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteProvider(provider.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showProviderForm && (
        <HealthProviderForm
          onClose={closeProviderForm}
          onSave={loadHealthData}
          fetchWithAuth={fetchWithAuth}
          editingProvider={editingProvider}
        />
      )}

      {/* Send Report Modal */}
      {showSendReportModal && (
        <SendReportModal
          onClose={() => setShowSendReportModal(false)}
          providers={providers}
          onSendReport={handleSendReport}
        />
      )}

      {/* Report Progress Modal */}
      {showReportProgressModal && (
        <ReportProgressModal
          progress={reportProgress}
          isComplete={reportComplete}
          onDismiss={() => {
            setShowReportProgressModal(false);
            setReportProgress(0);
            setReportComplete(false);
            setCurrentReportId(null);
          }}
        />
      )}

      {/* View Report Responses Modal */}
      {showReportResponsesModal && (
        <ViewReportResponsesModal
          onClose={() => setShowReportResponsesModal(false)}
          fetchWithAuth={fetchWithAuth}
        />
      )}

      {showManageReportsModal && (
        <ManageReportsModal
          onClose={() => setShowManageReportsModal(false)}
          fetchWithAuth={fetchWithAuth}
        />
      )}
        </>
      )}

      {/* Objective Form Modal (shared across tabs) */}
      {showObjectiveForm && (
        <ObjectiveForm
          onClose={closeObjectiveForm}
          onSave={loadHealthData}
          fetchWithAuth={fetchWithAuth}
          editingObjective={editingObjective}
        />
      )}
      </div>
    </div>
  );
}

export default HealthTab;
